import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { usePaginacao } from '../../hooks/usePaginacao';
import { useSortable } from '../../hooks/useSortable';
import SearchableSelect from '../../components/common/SearchableSelect';
import { useAuth } from '../../contexts/AuthContext';
import * as api from '../../api/clientes';
import * as empresasApi from '../../api/empresas';

/* ── CNPJ helpers ──────────────────────────────────────────── */
function maskCnpj(v) {
  const d = v.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 2)  return d;
  if (d.length <= 5)  return `${d.slice(0,2)}.${d.slice(2)}`;
  if (d.length <= 8)  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`;
  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`;
}

function isValidCnpj(raw) {
  const c = raw.replace(/\D/g, '');
  if (c.length !== 14) return false;
  if (/^(\d)\1+$/.test(c)) return false; // todos iguais
  const calc = (arr, w) => {
    const s = arr.reduce((acc, d, i) => acc + d * w[i], 0);
    const r = s % 11;
    return r < 2 ? 0 : 11 - r;
  };
  const d = c.split('').map(Number);
  return (
    calc(d.slice(0, 12), [5,4,3,2,9,8,7,6,5,4,3,2])  === d[12] &&
    calc(d.slice(0, 13), [6,5,4,3,2,9,8,7,6,5,4,3,2]) === d[13]
  );
}

/* ── CPF helpers ───────────────────────────────────────────── */
function maskCpf(v) {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3)  return d;
  if (d.length <= 6)  return `${d.slice(0,3)}.${d.slice(3)}`;
  if (d.length <= 9)  return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6)}`;
  return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
}

function isValidCpf(raw) {
  const c = raw.replace(/\D/g, '');
  if (c.length !== 11) return false;
  if (/^(\d)\1+$/.test(c)) return false;
  const calc = (len) => {
    let s = 0;
    for (let i = 0; i < len; i++) s += Number(c[i]) * (len + 1 - i);
    const r = (s * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return calc(9) === Number(c[9]) && calc(10) === Number(c[10]);
}

/* ── Estado inicial do form ────────────────────────────────── */
const VAZIO = { nome: '', tipo_pessoa: 'pj', cnpj: '', cpf: '', responsavel: '', email_contato: '', telefone: '' };

/* ── Vincular cliente já existente (colab) por CNPJ ──────────── */
function ModalVincular({ onClose, onVinculado, onCriarNovo }) {
  const [cnpj, setCnpj] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [vinculando, setVinculando] = useState(false);
  const [resultado, setResultado] = useState(null); // { id_cliente_final, nome, cnpj, ja_vinculado }
  const [erro, setErro] = useState('');
  const [naoEncontrado, setNaoEncontrado] = useState(false);

  const buscar = async (e) => {
    e.preventDefault();
    if (!isValidCnpj(cnpj)) { setErro('CNPJ inválido.'); return; }
    setErro(''); setResultado(null); setNaoEncontrado(false);
    setBuscando(true);
    try {
      const { data } = await api.buscarPorCnpj(cnpj.replace(/\D/g, ''));
      setResultado(data);
    } catch (err) {
      if (err.response?.status === 404) setNaoEncontrado(true);
      else setErro(err.response?.data?.detail || 'Erro ao buscar CNPJ.');
    } finally { setBuscando(false); }
  };

  const vincular = async () => {
    setVinculando(true);
    try {
      await api.vincularEmpresa(resultado.id_cliente_final);
      onVinculado();
    } catch (err) {
      setErro(err.response?.data?.detail || 'Erro ao vincular.');
    } finally { setVinculando(false); }
  };

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title mb-0">Vincular Cliente Existente</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <p className="text-muted" style={{ fontSize: 13 }}>
              Use quando o cliente já compra de outra empresa na plataforma e você quer atendê-lo
              também — em vez de cadastrar duplicado, vincule pelo CNPJ.
            </p>
            {erro && <div className="alert alert-danger py-2">{erro}</div>}

            <form onSubmit={buscar} className="d-flex gap-2 mb-3">
              <input
                className="form-control"
                value={cnpj}
                onChange={e => { setCnpj(maskCnpj(e.target.value)); setResultado(null); setNaoEncontrado(false); }}
                placeholder="00.000.000/0000-00"
                inputMode="numeric"
                autoFocus
              />
              <button type="submit" className="btn btn-outline-primary" disabled={buscando}>
                {buscando ? <span className="spinner-border spinner-border-sm" /> : 'Buscar'}
              </button>
            </form>

            {naoEncontrado && (
              <div className="alert alert-warning py-2 d-flex justify-content-between align-items-center">
                <span style={{ fontSize: 13 }}>Nenhum cliente encontrado com este CNPJ.</span>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onCriarNovo(cnpj)}>
                  Cadastrar como novo
                </button>
              </div>
            )}

            {resultado && (
              <div className="card">
                <div className="card-body d-flex justify-content-between align-items-center">
                  <div>
                    <div className="fw-semibold">{resultado.nome}</div>
                    <div className="text-muted" style={{ fontSize: 13 }}>{resultado.cnpj}</div>
                  </div>
                  {resultado.ja_vinculado ? (
                    <span className="badge bg-secondary">Já vinculado</span>
                  ) : (
                    <button className="btn btn-sm btn-primary" onClick={vincular} disabled={vinculando}>
                      {vinculando ? <span className="spinner-border spinner-border-sm" /> : 'Vincular'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Fechar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Modal({ item, empresas, emGlobal, onClose, onSaved }) {
  const editando = !!item?.id_cliente_final;
  const [form, setForm] = useState(editando ? {
    nome:          item.nome           || '',
    tipo_pessoa:   item.tipo_pessoa    || 'pj',
    cnpj:          item.cnpj           || '',
    cpf:           item.cpf            || '',
    responsavel:   item.responsavel    || '',
    email_contato: item.email_contato  || '',
    telefone:      item.telefone       || '',
  } : { ...VAZIO, cnpj: item?.cnpj || '', id_empresa_operadora: '' });
  const [cnpjErro, setCnpjErro] = useState('');
  const [cpfErro, setCpfErro] = useState('');
  const [loading, setLoading]   = useState(false);
  const [erro, setErro]         = useState('');
  const [empresasVinculo, setEmpresasVinculo] = useState(item?.empresas || []);
  const [novaEmpresaId, setNovaEmpresaId] = useState('');
  const [vinculando, setVinculando] = useState(false);
  const [erroVinculo, setErroVinculo] = useState('');

  const recarregarVinculos = async () => {
    const { data } = await api.buscarId(item.id_cliente_final);
    setEmpresasVinculo(data.empresas || []);
  };

  const handleVincular = async () => {
    if (!novaEmpresaId) return;
    setVinculando(true); setErroVinculo('');
    try {
      await api.vincularEmpresa(item.id_cliente_final, novaEmpresaId);
      setNovaEmpresaId('');
      await recarregarVinculos();
    } catch (err) {
      setErroVinculo(err.response?.data?.detail || 'Erro ao vincular.');
    } finally { setVinculando(false); }
  };

  const handleDesvincular = async (idEmpresa) => {
    if (!window.confirm('Desvincular este cliente dessa empresa?')) return;
    setVinculando(true); setErroVinculo('');
    try {
      await api.desvincularEmpresa(item.id_cliente_final, idEmpresa);
      await recarregarVinculos();
    } catch (err) {
      setErroVinculo(err.response?.data?.detail || 'Erro ao desvincular.');
    } finally { setVinculando(false); }
  };

  const set = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleCnpj = e => {
    const masked = maskCnpj(e.target.value);
    setForm(f => ({ ...f, cnpj: masked }));
    setCnpjErro('');
  };

  const validarCnpj = () => {
    if (form.cnpj && !isValidCnpj(form.cnpj))
      setCnpjErro('CNPJ inválido.');
    else
      setCnpjErro('');
  };

  const handleCpf = e => {
    const masked = maskCpf(e.target.value);
    setForm(f => ({ ...f, cpf: masked }));
    setCpfErro('');
  };

  const validarCpf = () => {
    if (form.cpf && !isValidCpf(form.cpf))
      setCpfErro('CPF inválido.');
    else
      setCpfErro('');
  };

  const salvar = async (e) => {
    e.preventDefault();
    setErro('');

    if (!editando && emGlobal && !form.id_empresa_operadora) {
      setErro('Selecione a empresa que está cadastrando este cliente.');
      return;
    }
    if (form.tipo_pessoa === 'pj' && form.cnpj && !isValidCnpj(form.cnpj)) {
      setCnpjErro('CNPJ inválido.');
      return;
    }
    if (form.tipo_pessoa === 'pf' && form.cpf && !isValidCpf(form.cpf)) {
      setCpfErro('CPF inválido.');
      return;
    }

    setLoading(true);
    try {
      const payload = { nome: form.nome, tipo_pessoa: form.tipo_pessoa };
      if (form.tipo_pessoa === 'pj' && form.cnpj) payload.cnpj = form.cnpj;
      if (form.tipo_pessoa === 'pf' && form.cpf)  payload.cpf  = form.cpf;
      if (form.responsavel)   payload.responsavel   = form.responsavel;
      if (form.email_contato) payload.email_contato = form.email_contato;
      if (form.telefone)      payload.telefone      = form.telefone;
      if (!editando && emGlobal) payload.id_empresa_operadora = form.id_empresa_operadora;

      if (editando) {
        await api.atualizar(item.id_cliente_final, payload);
      } else {
        await api.criar(payload);
      }
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      setErro(typeof d === 'string' ? d : 'Erro ao salvar. Verifique os dados.');
    } finally {
      setLoading(false);
    }
  };

  const toggleAtivo = async () => {
    setLoading(true);
    try {
      await api.atualizar(item.id_cliente_final, { ativo: !item.ativo });
      onSaved();
    } finally { setLoading(false); }
  };

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title mb-0">{editando ? 'Editar Cliente' : 'Novo Cliente'}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={salvar}>
            <div className="modal-body">
              {erro && <div className="alert alert-danger py-2 mb-3">{erro}</div>}

              {!editando && emGlobal && (
                <div className="mb-3">
                  <label className="form-label fw-semibold">Empresa *</label>
                  <SearchableSelect
                    options={empresas.map(e => ({ value: e.id_empresa_operadora, label: e.nome }))}
                    value={form.id_empresa_operadora}
                    onChange={v => setForm(f => ({ ...f, id_empresa_operadora: v }))}
                    placeholder="Selecione a empresa dona deste cadastro..." required />
                  <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                    Você está no modo global — escolha para qual empresa este cliente será vinculado.
                  </div>
                </div>
              )}

              {editando && emGlobal && (
                <div className="mb-3">
                  <label className="form-label fw-semibold">Empresas vinculadas</label>
                  {erroVinculo && <div className="alert alert-danger py-1 px-2 mb-2" style={{ fontSize: 12 }}>{erroVinculo}</div>}
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {empresasVinculo.filter(e => e.ativo).length === 0 && (
                      <span className="text-muted" style={{ fontSize: 12 }}>Nenhuma empresa vinculada.</span>
                    )}
                    {empresasVinculo.filter(e => e.ativo).map(e => (
                      <span key={e.id_empresa_operadora}
                        className="badge bg-light text-dark border d-flex align-items-center gap-1" style={{ fontSize: 12 }}>
                        {e.nome}
                        <button type="button" className="btn-close" style={{ fontSize: 8 }}
                          disabled={vinculando} onClick={() => handleDesvincular(e.id_empresa_operadora)} />
                      </span>
                    ))}
                  </div>
                  <div className="d-flex gap-2">
                    <div style={{ flex: 1 }}>
                      <SearchableSelect
                        options={empresas
                          .filter(emp => !empresasVinculo.some(e => e.id_empresa_operadora === emp.id_empresa_operadora && e.ativo))
                          .map(e => ({ value: e.id_empresa_operadora, label: e.nome }))}
                        value={novaEmpresaId}
                        onChange={setNovaEmpresaId}
                        placeholder="Vincular a outra empresa..." />
                    </div>
                    <button type="button" className="btn btn-sm btn-outline-primary"
                      disabled={!novaEmpresaId || vinculando} onClick={handleVincular}>
                      {vinculando ? <span className="spinner-border spinner-border-sm" /> : 'Vincular'}
                    </button>
                  </div>
                  <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                    Você está no modo global — pode vincular/desvincular este cliente de qualquer empresa.
                  </div>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label fw-semibold">Nome *</label>
                <input name="nome" className="form-control" value={form.nome} onChange={set} required
                  placeholder={form.tipo_pessoa === 'pf' ? 'Nome completo' : 'Razão social'} />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold d-block">Tipo</label>
                <div className="btn-group btn-group-sm">
                  <button type="button" className={`btn ${form.tipo_pessoa === 'pj' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setForm(f => ({ ...f, tipo_pessoa: 'pj' }))}>Empresa (PJ)</button>
                  <button type="button" className={`btn ${form.tipo_pessoa === 'pf' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setForm(f => ({ ...f, tipo_pessoa: 'pf' }))}>Pessoa física (PF)</button>
                </div>
              </div>

              <div className="row g-2 mb-3">
                <div className="col-6">
                  {form.tipo_pessoa === 'pj' ? (
                    <>
                      <label className="form-label fw-semibold">CNPJ</label>
                      <input
                        name="cnpj"
                        className={`form-control${cnpjErro ? ' is-invalid' : ''}`}
                        value={form.cnpj}
                        onChange={handleCnpj}
                        onBlur={validarCnpj}
                        placeholder="00.000.000/0000-00"
                        inputMode="numeric"
                      />
                      {cnpjErro && <div className="invalid-feedback">{cnpjErro}</div>}
                    </>
                  ) : (
                    <>
                      <label className="form-label fw-semibold">CPF</label>
                      <input
                        name="cpf"
                        className={`form-control${cpfErro ? ' is-invalid' : ''}`}
                        value={form.cpf}
                        onChange={handleCpf}
                        onBlur={validarCpf}
                        placeholder="000.000.000-00"
                        inputMode="numeric"
                      />
                      {cpfErro && <div className="invalid-feedback">{cpfErro}</div>}
                    </>
                  )}
                </div>
                <div className="col-6">
                  <label className="form-label fw-semibold">Telefone</label>
                  <input name="telefone" className="form-control" value={form.telefone} onChange={set} placeholder="(11) 99999-9999" />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Responsável</label>
                <input name="responsavel" className="form-control" value={form.responsavel} onChange={set} placeholder="Nome do responsável" />
              </div>

              <div className="mb-1">
                <label className="form-label fw-semibold">E-mail</label>
                <input name="email_contato" type="email" className="form-control" value={form.email_contato} onChange={set} />
              </div>
            </div>

            <div className="modal-footer d-flex justify-content-between">
              {editando ? (
                <button type="button"
                  className={`btn btn-sm ${item.ativo ? 'btn-outline-danger' : 'btn-outline-success'}`}
                  onClick={toggleAtivo} disabled={loading}>
                  {item.ativo ? 'Desativar' : 'Ativar'}
                </button>
              ) : <span />}
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancelar</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                  {loading ? <span className="spinner-border spinner-border-sm" /> : 'Salvar'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function Clientes() {
  const { user } = useAuth();
  const souAdmin = user?.perfil === 'admin';
  const emGlobal = souAdmin && !user?.empresa_ativa_id;

  const [items, setItems]   = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [idEmpresaFiltro, setIdEmpresaFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [busca, setBusca]   = useState('');
  const [modal, setModal]   = useState(null);
  const [modalVincular, setModalVincular] = useState(false);
  const [erroCarregar, setErroCarregar] = useState('');
  const [desvinculando, setDesvinculando] = useState(null);

  const carregar = useCallback(() => {
    setLoading(true);
    setErroCarregar('');
    api.listar()
      .then(r => setItems(r.data))
      .catch(() => setErroCarregar('Erro ao carregar clientes. Tente novamente.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (souAdmin) empresasApi.listEmpresas().then(r => setEmpresas(r.data)).catch(() => {});
  }, [souAdmin]);

  const desvincular = async (cliente) => {
    if (!window.confirm(`Desvincular "${cliente.nome}" da sua empresa?\n\nEle deixará de aparecer no seu catálogo/pedidos, mas continua existindo na plataforma (vinculado a outras empresas, se houver).`)) return;
    setDesvinculando(cliente.id_cliente_final);
    try {
      await api.desvincularEmpresa(cliente.id_cliente_final);
      carregar();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao desvincular.');
    } finally { setDesvinculando(null); }
  };

  const filtrados = items.filter(i => {
    const matchBusca =
      i.nome.toLowerCase().includes(busca.toLowerCase()) ||
      (i.cnpj          || '').includes(busca) ||
      (i.cpf           || '').includes(busca) ||
      (i.email_contato || '').toLowerCase().includes(busca.toLowerCase());
    const matchEmpresa = emGlobal && idEmpresaFiltro
      ? (i.empresas || []).some(e => e.id_empresa_operadora === idEmpresaFiltro)
      : true;
    return matchBusca && matchEmpresa;
  });

  const { sorted, Th } = useSortable(filtrados, 'nome');
  const { paginados, Paginacao } = usePaginacao(sorted);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader titulo="Clientes Finais" subtitulo={`${items.length} cadastrados`}>
        <button className="btn btn-outline-primary btn-sm me-2" onClick={() => setModalVincular(true)}>
          Vincular Existente
        </button>
        <button className="btn btn-primary btn-sm" onClick={() => setModal({})}>+ Novo Cliente</button>
      </PageHeader>

      {erroCarregar && <div className="alert alert-danger">{erroCarregar}</div>}

      <div className="d-flex gap-2 mb-3 flex-wrap">
        <input className="form-control" style={{ maxWidth: 360 }}
          placeholder="Buscar por nome, CNPJ ou e-mail..."
          value={busca} onChange={e => setBusca(e.target.value)} />
        {emGlobal && (
          <select className="form-select" style={{ maxWidth: 220 }}
            value={idEmpresaFiltro} onChange={e => setIdEmpresaFiltro(e.target.value)}>
            <option value="">Todas as empresas</option>
            {empresas.map(e => <option key={e.id_empresa_operadora} value={e.id_empresa_operadora}>{e.nome}</option>)}
          </select>
        )}
      </div>

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <Th campo="nome">Nome</Th>
                  <th>Tipo</th>
                  <th>CNPJ/CPF</th>
                  <th>Empresa(s)</th>
                  <Th campo="email_contato">E-mail</Th>
                  <Th campo="telefone">Telefone</Th>
                  <Th campo="ativo">Status</Th>
                  <th style={{ width: 140 }}></th>
                </tr>
              </thead>
              <tbody>
                {paginados.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-muted py-4">Nenhum cliente encontrado.</td></tr>
                )}
                {paginados.map(c => (
                  <tr key={c.id_cliente_final}>
                    <td className="fw-semibold">{c.nome}</td>
                    <td>
                      <span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>
                        {c.tipo_pessoa === 'pf' ? 'PF' : 'PJ'}
                      </span>
                    </td>
                    <td>{c.tipo_pessoa === 'pf' ? (c.cpf || '—') : (c.cnpj || '—')}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {(c.empresas || []).length === 0 && '—'}
                        {(c.empresas || []).map(e => (
                          <span key={e.id_empresa_operadora}
                            className={`badge ${e.ativo ? 'bg-light text-dark border' : 'bg-secondary'}`}
                            style={{ fontSize: 11 }}>
                            {e.nome}{!e.ativo ? ' (desvinculado)' : ''}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>{c.email_contato || '—'}</td>
                    <td>{c.telefone || '—'}</td>
                    <td><StatusBadge status={c.ativo ? 'ativo' : 'inativo'} /></td>
                    <td>
                      <div className="d-flex gap-1">
                        <button className="btn btn-sm btn-outline-primary py-0 px-2"
                          onClick={() => setModal(c)}>Editar</button>
                        {!emGlobal && (
                          <button className="btn btn-sm btn-outline-danger py-0 px-2"
                            disabled={desvinculando === c.id_cliente_final}
                            onClick={() => desvincular(c)}>
                            {desvinculando === c.id_cliente_final ? '…' : 'Desvincular'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Paginacao />

      {modalVincular && (
        <ModalVincular
          onClose={() => setModalVincular(false)}
          onVinculado={() => { setModalVincular(false); carregar(); }}
          onCriarNovo={(cnpj) => { setModalVincular(false); setModal({ cnpj }); }}
        />
      )}

      {modal !== null && (
        <Modal
          item={modal}
          empresas={empresas}
          emGlobal={emGlobal}
          onClose={() => { setModal(null); carregar(); }}
          onSaved={() => { setModal(null); carregar(); }}
        />
      )}
    </div>
  );
}
